import classNames from 'classnames';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Event, EventStatus, EventUserStatus } from '../../../../../services';
import { isEventPast } from '../../../../utils';
import { hasEventStatusBadges } from '../../helpers';

import classes from './EventStatusBadges.module.scss';

interface EventStatusBadgesProps {
  event: Event;
}

export const EventStatusBadges = memo(({ event }: EventStatusBadgesProps) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'eventItem' });

  const {
    allowWaitlist,
    endAt,
    participantCountConfirmed,
    participantsCapacity,
    participantStatus,
    startAt,
    status,
  } = event;

  const isPast = useMemo(() => isEventPast(startAt, endAt), [startAt, endAt]);

  const isCancelled = useMemo(() => status === EventStatus.CANCELLED, [status]);

  const isAttending = useMemo(
    () => participantStatus === EventUserStatus.CONFIRMED,
    [participantStatus]
  );

  const isHost = useMemo(() => participantStatus === EventUserStatus.HOST, [participantStatus]);

  const isWaitlisted = useMemo(
    () => participantStatus === EventUserStatus.WAITLISTED,
    [participantStatus]
  );

  const isFull = useMemo(
    () => participantsCapacity > 0 && participantCountConfirmed >= participantsCapacity,
    [participantsCapacity, participantCountConfirmed]
  );

  const hasPersonalStatus = isHost || isAttending || isWaitlisted;

  if (!hasEventStatusBadges(event)) {
    return null;
  }

  if (isCancelled) {
    return (
      <div className={classes['status-badges']}>
        {isHost && (
          <span className={classNames(classes['status-badge'], classes['status-badge--host'])}>
            {ctxT('host')}
          </span>
        )}
        <span className={classNames(classes['status-badge'], classes['status-badge--cancelled'])}>
          {ctxT('cancelled')}
        </span>
      </div>
    );
  }

  return (
    <div className={classes['status-badges']}>
      {isHost && (
        <span className={classNames(classes['status-badge'], classes['status-badge--host'])}>
          {ctxT('host')}
        </span>
      )}
      {isAttending && (
        <span className={classNames(classes['status-badge'], classes['status-badge--attending'])}>
          {ctxT('attending')}
        </span>
      )}
      {isWaitlisted && (
        <span className={classNames(classes['status-badge'], classes['status-badge--waitlist'])}>
          {ctxT('waitlist')}
        </span>
      )}
      {isFull && !hasPersonalStatus && !isPast && (
        <span className={classNames(classes['status-badge'], classes['status-badge--full'])}>
          {allowWaitlist ? ctxT('full-waitlist-open') : ctxT('full')}
        </span>
      )}
      {isPast && (
        <span className={classNames(classes['status-badge'], classes['status-badge--past'])}>
          {ctxT('past')}
        </span>
      )}
    </div>
  );
});
